export const BLOCK_TYPES = [
  'IMAGE',
  'VIDEO',
  'CONTENT',
  'SLIDER',
  'FORM',
  'COUNTDOWN',
  'BANK_ACCOUNTS',
] as const
export type BlockType = (typeof BLOCK_TYPES)[number]

export interface IBlockPayloadImage {
  url: string
  alt?: string
  caption?: string
}

export interface IBlockPayloadVideo {
  url: string
  /** ความกว้าง (px หรือ %) เช่น 560, 100% */
  width?: string
  /** ความสูง (px) เช่น 315 — ไม่ใช้เมื่อ fullWidth */
  height?: string
  /** full width = 100%, ความสูงปรับอัตโนมัติตาม aspect ratio 16:9 */
  fullWidth?: boolean
}

export interface IBlockPayloadContent {
  html?: string
}

export interface IBlockPayloadSlider {
  images: Array<{ url: string; alt?: string }>
}

export interface ISalepageFormProduct {
  objectId: string
  code: string
  name: string
  image?: string
  price: number
}

export interface IBlockPayloadForm {
  /** รายการสินค้าที่เลือกจากระบบ */
  products?: ISalepageFormProduct[]
  /** ข้อความปุ่มสั่งซื้อ */
  btnText?: string
}

export interface ICountdownColors {
  /** พื้นหลังหลัก — CSS value เช่น solid หรือ linear-gradient */
  bg?: string
  /** เปิดใช้ gradient mode */
  bgGradient?: boolean
  /** สี gradient เริ่มต้น */
  bgFrom?: string
  /** สี gradient สิ้นสุด */
  bgTo?: string
  /** องศา gradient */
  bgAngle?: number
  /** สีพื้นหลังของตัวเลข */
  digitBg?: string
  /** สีตัวเลขนาฬิกา */
  digitColor?: string
  /** สีข้อความ title + label (วัน ชม. นาที วินาที) */
  textColor?: string
  /** สี icon นาฬิกา */
  iconColor?: string
}

export interface IBlockPayloadCountdown {
  /** วันเวลาที่หมดอายุ (ISO 8601) */
  expireAt: string
  /** ข้อความหัว เช่น "โปรโมชั่นสิ้นสุดใน" */
  title?: string
  /** ข้อความเมื่อหมดเวลา */
  expiredText?: string
  /** ปรับแต่งสี */
  colors?: ICountdownColors
}

export interface IBankAccountRow {
  /** รหัสธนาคาร เช่น SCB, KBANK — ใช้แสดงไอคอนจาก /images/bank-icons/{code}.png */
  bankCode?: string
  bankName: string
  accountName: string
  accountNumber: string
  branch?: string
}

export interface IBlockPayloadBankAccounts {
  title?: string
  accounts: IBankAccountRow[]
}

export type BlockPayload =
  | IBlockPayloadImage
  | IBlockPayloadVideo
  | IBlockPayloadContent
  | IBlockPayloadSlider
  | IBlockPayloadForm
  | IBlockPayloadCountdown
  | IBlockPayloadBankAccounts

export interface IBlockSettings {
  width?: string
  alignment?: string
  [key: string]: unknown
}

export interface IBlock {
  type: BlockType
  order: number
  payload: BlockPayload
  settings?: IBlockSettings
}

export const BLOCK_TYPE_LABELS: Record<BlockType, string> = {
  IMAGE: 'รูปภาพ',
  VIDEO: 'วิดีโอ',
  CONTENT: 'เนื้อหา',
  SLIDER: 'สไลด์ภาพ',
  FORM: 'ฟอร์ม',
  COUNTDOWN: 'นับถอยหลัง',
  BANK_ACCOUNTS: 'บัญชีธนาคาร',
}

export const BLOCK_TYPE_META: Record<
  BlockType,
  { label: string; icon: string; color: string; desc: string }
> = {
  IMAGE: {
    label: 'รูปภาพ',
    icon: 'image',
    color: '#6EE7B7',
    desc: 'อัพโหลดหรือใส่ URL รูปภาพ',
  },
  VIDEO: {
    label: 'วีดีโอ',
    icon: 'video_library',
    color: '#F472B6',
    desc: 'ลิงก์ YouTube',
  },
  CONTENT: {
    label: 'เนื้อหา',
    icon: 'text_fields',
    color: '#34D399',
    desc: 'แก้ไขเนื้อหาแบบ WYSIWYG',
  },
  SLIDER: {
    label: 'สไลด์รูปภาพ',
    icon: 'view_carousel',
    color: '#FBBF24',
    desc: 'แสดงผลแบบ Carousel',
  },
  FORM: {
    label: 'ฟอร์ม',
    icon: 'shopping_cart',
    color: '#A78BFA',
    desc: 'ฟอร์มสำเร็จรูป',
  },
  COUNTDOWN: {
    label: 'นับถอยหลัง',
    icon: 'timer',
    color: '#F97316',
    desc: 'นาฬิกานับถอยหลัง',
  },
  BANK_ACCOUNTS: {
    label: 'บัญชีธนาคาร',
    icon: 'account_balance',
    color: '#0EA5E9',
    desc: 'แสดงหลายบัญชีสำหรับโอนเงิน',
  },
}
